import type { CameraDefinition } from "daedalus-core";
import { VISION_CAP_TILES, WORLD_TILE_PIXELS, type Size } from "../vision/vision.ts";

/**
 * OdysseyTales's camera for the editor's camera-stop guide (P1.6-T47). The game's world camera is bounded by
 * the map and centred on the local character, so it stops following half its view from each edge. Its view
 * at the default zoom depends on the screen (ADR-061 decision 6: 20 by 11.25 tiles at 1280 by 720, 24 by
 * 13.5 at 1920 by 1080), and never exceeds the vision cap, since the default zoom is the nearest allowed zoom
 * whose view fits inside it. The registered view is the cap itself: the widest view any screen shows at its
 * default zoom, the one every player's vision is held to, and exactly the view on `CAP_SCREEN`. The band the
 * editor draws is therefore where the camera stops on the widest default view; a smaller screen's camera
 * stops nearer the edge, inside the band.
 */

/**
 * The camera: the vision cap in world pixels, 27 by 15 tiles of 32 pixels, 864 by 480.
 */
export const ODYSSEY_CAMERA: CameraDefinition = {
  view: { width: VISION_CAP_TILES.width * WORLD_TILE_PIXELS, height: VISION_CAP_TILES.height * WORLD_TILE_PIXELS },
};

/**
 * The world zoom at which a screen's default view is exactly the vision cap: 3, the zoom ADR-061 decision 6
 * measures the cap at.
 */
const CAP_ZOOM = 3;

/**
 * The screen, in device pixels, whose default zoom (3) shows exactly the vision cap: 2592 by 1440. A browser
 * check of the guide against the game's camera runs the game at this size.
 */
export const CAP_SCREEN: Size = Object.freeze({ width: ODYSSEY_CAMERA.view.width * CAP_ZOOM, height: ODYSSEY_CAMERA.view.height * CAP_ZOOM });
